import React from 'react';
import { useLanguage } from '@/LanguageContext';
import { motion } from 'framer-motion';
import { Target, Heart, Shield, CheckCircle } from 'lucide-react';

const About = () => {
  const { t } = useLanguage();

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  const valueIcons = [Shield, Heart, Target, CheckCircle];

  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <motion.p
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
              className="text-[#C5A55A] text-sm font-mono tracking-[0.3em] uppercase mb-4"
            >
              {t('about.overline')}
            </motion.p>

            <motion.h2
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
              className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-[#0A1628] mb-8"
              data-testid="about-title"
            >
              {t('about.title')}
            </motion.h2>

            <motion.p
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
              className="text-slate-600 text-base md:text-lg leading-relaxed mb-10"
            >
              {t('about.description')}
            </motion.p>

            <motion.div
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={3}
              className="border-l-4 border-[#C5A55A] pl-6 mb-8"
            >
              <h3 className="font-heading text-xl font-semibold text-[#0A1628] mb-2">{t('about.mission.title')}</h3>
              <p className="text-slate-600 leading-relaxed">{t('about.mission.text')}</p>
            </motion.div>

            <motion.div
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={4}
              className="bg-[#F4F7FB] p-8"
            >
              <h3 className="font-heading text-xl font-semibold text-[#0A1628] mb-2">{t('about.guarantee.title')}</h3>
              <p className="text-slate-600 leading-relaxed">{t('about.guarantee.text')}</p>
            </motion.div>
          </div>

          <div className="relative">
            <motion.div
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
              className="relative z-10"
            >
              <img
                src="https://static.prod-images.emergentagent.com/jobs/2654ee85-ac0d-4b04-a252-c1374ec6c4f8/images/f17bb3e57d157955e16635849713139a573d681755f485eadcbe2152ecafcdcd.png"
                alt="Medical professional"
                className="w-full h-[500px] object-cover shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#0A1628] p-6 shadow-xl">
                <p className="text-[#C5A55A] font-heading text-3xl font-bold">10+</p>
                <p className="text-white text-sm tracking-wider">Years of Excellence</p>
              </div>
            </motion.div>

            <motion.div
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={5}
              className="mt-12"
            >
              <h3 className="font-heading text-xl font-semibold text-[#0A1628] mb-4">{t('about.values.title')}</h3>
              <div className="grid grid-cols-2 gap-4">
                {t('about.values.items').map((item, i) => {
                  const Icon = valueIcons[i];
                  return (
                    <div key={i} className="flex items-center gap-3 p-3 bg-[#F4F7FB] hover:bg-[#E3E9F2] transition-colors">
                      <Icon className="w-5 h-5 text-[#C5A55A] flex-shrink-0" />
                      <span className="text-[#0A1628] text-sm font-medium">{item}</span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
